import { useState, useEffect, useCallback, useRef } from "react";
import { TIER_UP, TIER_DOWN } from "../lib/constants";
import { fetchNewsFromWorker, postOverride as apiPostOverride, fetchDeepAnalysis, fetchSubScenarios, fetchAutoIdeas, generateCustomIdeas } from "../lib/api";
import { loadClsCache, loadDeepCache, saveDeepCache, loadIdeaCache, saveIdeaCache } from "../lib/cache";
import { byImportance } from "../lib/utils";

const DAILY_CAP_MSG = "오늘 취재 아이디어 한도 6회를 모두 사용했습니다 (자정 리셋)";
const RATE_LIMIT_MSG = "요청이 너무 빠릅니다. 잠시 후 다시 시도해 주세요";

function ideaErrorMessage(err) {
  if (err?.code === "daily_cap") return DAILY_CAP_MSG;
  if (err?.code === "rate_limit") return RATE_LIMIT_MSG;
  return err?.message || "알 수 없는 오류";
}

export default function useDesertEye() {
  const [tab, setTab] = useState("news");
  const [time, setTime] = useState(new Date());

  const [news, setNews]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [lastFetch, setLastFetch] = useState(null);

  const [ach, setAch]                   = useState([]);
  const [cuiBono, setCuiBono]           = useState("");
  const [scenarios, setScenarios]       = useState([]);
  const [topArticles, setTopArticles]   = useState([]);
  const [adversaryFrame, setAdversaryFrame] = useState(null);
  const [themes, setThemes] = useState([]);
  const [subScenariosMap, setSubScenariosMap]   = useState({});
  const [subLoadingIdx, setSubLoadingIdx]       = useState(null);
  const [deepLoading, setDeepLoading]   = useState(false);
  const [deepError, setDeepError]       = useState("");

  const [sel, setSel] = useState(null);

  const [autoIdeas, setAutoIdeas]   = useState(null);
  const [autoIdeaL, setAutoIdeaL]   = useState(false);
  const [ideaIn, setIdeaIn]     = useState("");
  const [ideas, setIdeas]       = useState(null);
  const [ideaL, setIdeaL]       = useState(false);
  const [ideaErr, setIdeaErr]   = useState("");

  const [unverifiedPanel, setUnverifiedPanel] = useState("socmint");

  const panelRef     = useRef(null);
  const deepDoneRef  = useRef(false);
  const ideaDoneRef  = useRef(false);
  const newsRef      = useRef([]);

  // Clock
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const runDeep = useCallback(async () => {
    if (deepDoneRef.current) return;
    deepDoneRef.current = true;
    setDeepLoading(true); setDeepError("");
    try {
      const result = await fetchDeepAnalysis();
      if (result) {
        saveDeepCache(result);
        if (Array.isArray(result.ach)) setAch(result.ach);
        if (result.cuiBono) setCuiBono(result.cuiBono);
        if (Array.isArray(result.scenarios)) setScenarios(result.scenarios);
        if (Array.isArray(result.topArticles)) setTopArticles(result.topArticles);
        if (result.adversaryFrame) setAdversaryFrame(result.adversaryFrame);
        if (result.themes?.length > 0) setThemes(result.themes);
      } else {
        const cached = loadDeepCache();
        if (cached) {
          if (Array.isArray(cached.ach)) setAch(cached.ach);
          if (cached.cuiBono) setCuiBono(cached.cuiBono);
          if (Array.isArray(cached.scenarios)) setScenarios(cached.scenarios);
          if (Array.isArray(cached.topArticles)) setTopArticles(cached.topArticles);
          if (cached.adversaryFrame) setAdversaryFrame(cached.adversaryFrame);
          if (cached.themes?.length > 0) setThemes(cached.themes);
        } else {
          deepDoneRef.current = false;
        }
      }
    } catch (e) {
      deepDoneRef.current = false;
      setDeepError(e.message || "알 수 없는 오류");
    }
    setDeepLoading(false);
  }, []);

  const runIdeas = useCallback(async () => {
    if (ideaDoneRef.current) return;
    ideaDoneRef.current = true;
    setAutoIdeaL(true);
    try {
      const workerIdeas = await fetchAutoIdeas();
      if (workerIdeas) {
        setAutoIdeas(workerIdeas);
        saveIdeaCache(workerIdeas);
        setAutoIdeaL(false);
        return;
      }
      const cached = loadIdeaCache();
      if (cached) {
        setAutoIdeas(cached);
        setAutoIdeaL(false);
        return;
      }
      ideaDoneRef.current = false;
    } catch {
      ideaDoneRef.current = false;
    }
    setAutoIdeaL(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setAch([]); setScenarios([]); setTopArticles([]);
    setSubScenariosMap({}); setSubLoadingIdx(null);
    setDeepError(""); setAutoIdeas(null); setAutoIdeaL(false);
    deepDoneRef.current = false;
    ideaDoneRef.current = false;

    try {
      const { articles: raw, lastUpdated, themes: fetchedThemes } = await fetchNewsFromWorker();
      const clsCache = loadClsCache();
      const articles = raw.map(a => {
        if (a.reasoning && a.hed_ko) return a;
        if (clsCache[a.id]) return { ...a, ...clsCache[a.id] };
        return a;
      });

      setNews(articles);
      if (fetchedThemes?.length > 0) setThemes(fetchedThemes);
      setLastFetch(lastUpdated ? new Date(lastUpdated) : new Date());
      setLoading(false);
      newsRef.current = articles;

      runDeep();
      runIdeas();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [runDeep, runIdeas]);

  useEffect(() => { load(); }, [load]);

  // 30min auto refresh
  useEffect(() => {
    const interval = setInterval(() => { load(); }, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  const genMoreIdeas = useCallback(async (idea) => {
    try {
      const { ideas: result } = await generateCustomIdeas(idea.title, "derive");
      return Array.isArray(result) ? result : [];
    } catch (err) {
      setIdeaErr(ideaErrorMessage(err));
      return [];
    }
  }, []);

  const genIdeas = useCallback(async () => {
    if (!ideaIn.trim()) return;
    setIdeaL(true); setIdeas(null); setIdeaErr("");
    try {
      const { ideas: result } = await generateCustomIdeas(ideaIn.trim(), "fresh");
      if (result?.length > 0) setIdeas(result);
      else setIdeaErr("응답 없음");
    } catch (err) { setIdeaErr(ideaErrorMessage(err)); }
    setIdeaL(false);
  }, [ideaIn]);

  const handleOverride = useCallback(async (article, direction) => {
    const newCls = await apiPostOverride(article, direction, TIER_UP, TIER_DOWN);
    if (newCls) {
      setNews(prev => prev.map(n => n.id === article.id ? { ...n, cls: newCls } : n));
      setSel(prev => prev && prev.id === article.id ? { ...prev, cls: newCls } : prev);
    }
  }, []);

  const closeSel = useCallback(() => setSel(null), []);

  const handleLoadSubScenarios = useCallback(async (scenarioIdx, scenario) => {
    setSubLoadingIdx(scenarioIdx);
    try {
      const subs = await fetchSubScenarios(scenario, topArticles);
      setSubScenariosMap(prev => ({ ...prev, [scenarioIdx]: subs }));
    } catch (e) {
      const msg = e.message || "";
      setSubScenariosMap(prev => ({ ...prev, [scenarioIdx]: [{ title: msg.includes("429") ? "API 한도 초과 — 잠시 후 재시도" : "분석 실패", probability: "—", detail: msg }] }));
    }
    setSubLoadingIdx(null);
  }, [topArticles]);

  const switchTab = useCallback((id) => {
    setTab(id);
    if (panelRef.current) panelRef.current.scrollTop = 0;
    if (id === "deeper" && !deepDoneRef.current) runDeep();
    if (id === "idea" && !ideaDoneRef.current) runIdeas();
  }, [runDeep, runIdeas]);

  // Derived data
  const mainNews = news.filter(n => n.origin === "rsshub");
  const byTier = {
    gun:      mainNews.filter(n => n.cls === "gun"),
    watershed:mainNews.filter(n => n.cls === "watershed"),
    notable:  mainNews.filter(n => n.cls === "notable"),
    common:   mainNews.filter(n => n.cls === "common"),
  };
  const socmint = byImportance(news.filter(n => n.origin === "telegram" && ["osint", "socmint"].includes(n.type)));
  const adversary = byImportance(news.filter(n => n.origin === "telegram" && n.type === "adversary"));

  return {
    // State
    tab, time, news, loading, error, lastFetch,
    ach, cuiBono, scenarios, topArticles, subScenariosMap, subLoadingIdx,
    deepLoading, deepError,
    sel, autoIdeas, autoIdeaL, ideaIn, ideas, ideaL, ideaErr,
    unverifiedPanel,
    // Refs
    panelRef,
    // Derived
    mainNews, byTier, socmint, adversary, adversaryFrame, themes,
    // Actions
    switchTab, load, genIdeas, genMoreIdeas, handleOverride, closeSel,
    setSel, setIdeaIn, setUnverifiedPanel, handleLoadSubScenarios,
  };
}
